import { MouseEventHandler } from 'react';
import { openConsentPreferences, useConsentLawActive } from '@v2/shared/lib/cookieYes';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

type Item = { href: string; title: string; onClick?: MouseEventHandler<HTMLAnchorElement> };
type Menu = { title: string; items: Item[] };

/**
 * headless: apps/topmags/layouts/MainLayout/Footer/hooks/useFooterMenu.
 *
 * The paths are TopMags' own url templates from its headless config, spelled out here
 * because there is no config object to read them from -- V2 resolves a site's urls
 * through useUrlGenerator, which handles the base url but not a named template.
 *
 * Every href is a PageRegistry slug, the same ones the institutional menu lists — which is
 * what lets that menu mark the entry a visitor arrived on. They used to be TopMags' own
 * `/page/<template>.html` paths, and before that two of them were DiscountMags' spellings
 * and 404'd; the brands share the slugs now, so neither mismatch is possible again.
 */
export default function useFooterMenu(): { menu: Menu[] } {
  const { buildUrl } = useUrlGenerator();
  const consentLawActive = useConsentLawActive();

  return {
    menu: [
      {
        title: 'About:',
        items: [
          { href: buildUrl('/about-us'), title: 'About TopMags' },
          { href: buildUrl('/contact'), title: 'Contact Us' },
          { href: buildUrl('/help'), title: 'F.A.Q.' },
        ],
      },
      {
        title: 'More Info:',
        items: [
          { href: buildUrl('/terms'), title: 'Terms of Use' },
          { href: buildUrl('/privacy'), title: 'Privacy Notice' },
          // Only where CookieYes says a privacy law applies — see useConsentLawActive.
          // Cal. Civ. Code 1798.135(a)(1) names the link, so the title is not ours to
          // shorten to fit a row whose other entries are two or three words; it wraps to
          // two lines here, as they already do on a phone. The click opens the preference
          // centre; the href is where it lands if CookieYes cannot be asked.
          ...(consentLawActive
            ? [
                {
                  href: buildUrl('/privacy#your-choices'),
                  title: 'Do Not Sell or Share My Personal Information',
                  onClick: openConsentPreferences,
                },
              ]
            : []),
          { href: buildUrl('/accessibility'), title: 'Accessibility Statement' },
        ],
      },
    ],
  };
}
